import React, { useState, useEffect } from 'react';
import { useLoaderData, useParams, useSearchParams } from 'react-router-dom';
import { Dropdown, Message } from '../../components/PrimereactComponents';
import { capitalizeFirstLetter, getBodyNameGenitive } from '../../utils/texts';
import { useTranslation } from 'react-i18next';
import Intro from '../../components/common/Intro/Intro';
import Section from '../../components/common/Section/Section';
import { DropdownChangeParams } from 'primereact/dropdown';
import { MeetingCard } from '../../components/meetings/MeetingCard';
import { SessionsDataResponseType } from '../../types';
import { URLKeys } from '../../Constants';

function Meetings() {
    const { t } = useTranslation(['translation', 'meetings']);
    const [searchParams, setSearchParams] = useSearchParams();
    const { bodyType } = useParams();

    const {
        meetingsData: sessionsData,
        municipalityData: municipality,
        dropdownYearOptions
    } = useLoaderData() as {
        meetingsData: SessionsDataResponseType[];
        municipalityData: any;
        dropdownYearOptions: { name: string; code: string }[];
    };

    const sortByOptions = [
        { name: t('sort-by-date-asc', { ns: 'meetings' }), code: 'datum-vzest' },
        { name: t('sort-by-date-desc', { ns: 'meetings' }), code: 'datum-sest' },
        { name: t('sort-by-votes-asc', { ns: 'meetings' }), code: 'pocet_platnych_hlasovani-vzest' },
        { name: t('sort-by-votes-desc', { ns: 'meetings' }), code: 'pocet_platnych_hlasovani-sest' },
        { name: t('sort-by-attendance-asc', { ns: 'meetings' }), code: 'ucast-vzest' },
        { name: t('sort-by-attendance-desc', { ns: 'meetings' }), code: 'ucast-sest' }
    ];
    const getYearOption = (year: string | null) => {
        return dropdownYearOptions.find((option) => option.code === year) || undefined;
    };

    const [filteringState, setFilteringState] = useState({
        selectedYear: getYearOption(searchParams.get(URLKeys.selectedYear)),
        sortBy: sortByOptions.find((option) => option.code === searchParams.get(URLKeys.sortBy)) || sortByOptions[0]
    });

    const handleFilteringChange = (e: DropdownChangeParams, key: keyof typeof filteringState) => {
        const newFilteringState = { ...filteringState, [key]: e.value };
        setFilteringState(newFilteringState);
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set(URLKeys.sortBy, newFilteringState.sortBy.code);
            if (newFilteringState.selectedYear) {
                newParams.set(URLKeys.selectedYear, newFilteringState.selectedYear.code);
            } else {
                newParams.delete(URLKeys.selectedYear);
            }
            return newParams;
        });
    };

    const sortSessionsData = (data: SessionsDataResponseType[], sortBy: { code: string }) => {
        if (!sortBy) return data;

        const [key, order] = sortBy.code.split('-');
        return data.sort((a, b) => {
            if (key === 'datum') {
                const dateA = new Date(a[key].split('.').reverse().join('-'));
                const dateB = new Date(b[key].split('.').reverse().join('-'));
                return order === 'vzest' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
            } else {
                return order === 'vzest'
                    ? (a[key as keyof SessionsDataResponseType] as number) -
                          (b[key as keyof SessionsDataResponseType] as number)
                    : (b[key as keyof SessionsDataResponseType] as number) -
                          (a[key as keyof SessionsDataResponseType] as number);
            }
        });
    };

    useEffect(() => {
        setSearchParams(
            (prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set(URLKeys.sortBy, filteringState.sortBy.code);
                if (
                    filteringState.selectedYear &&
                    searchParams.get(URLKeys.selectedYear) !== filteringState.selectedYear.code
                ) {
                    newParams.set(URLKeys.selectedYear, filteringState.selectedYear.code);
                } else if (!filteringState.selectedYear) {
                    newParams.delete(URLKeys.selectedYear);
                }
                return newParams;
            },
            { replace: true }
        );
    }, [filteringState.selectedYear, filteringState.sortBy.code, searchParams, setSearchParams]);

    if (sessionsData !== undefined && municipality !== undefined && dropdownYearOptions !== undefined) {
        const sortedSessionsData = sortSessionsData(sessionsData, filteringState.sortBy);

        const filteringComponents = (
            <>
                <span className='p-float-label'>
                    <Dropdown
                        showClear
                        value={filteringState.selectedYear}
                        options={dropdownYearOptions}
                        onChange={(e) => handleFilteringChange(e, 'selectedYear')}
                        optionLabel='name'
                        inputId='selectedYear'
                    />
                    <label htmlFor='selectedYear'>{t('select-year-meeting')}</label>
                </span>
                <span className='p-float-label'>
                    <Dropdown
                        value={filteringState.sortBy}
                        options={sortByOptions}
                        onChange={(e) => handleFilteringChange(e, 'sortBy')}
                        optionLabel='name'
                        inputId='sortBy'
                    />
                    <label htmlFor='sortBy'>{t('sort-by')}</label>
                </span>
            </>
        );

        const subtitle = `${t('in-term', {
            ns: 'meetings',
            num: sessionsData.length,
            where: bodyType ? municipality.organy[bodyType].misto : '',
            type: getBodyNameGenitive(bodyType)
        })}`;

        return (
            <>
                <Intro title={capitalizeFirstLetter(t('meetings'))} description={subtitle} />
                <Section contentClassName='filtering'>{filteringComponents}</Section>
                <div className='card-list'>
                    {sortedSessionsData.length === 0 ? (
                        <Message severity='info' text={t('no-data-found', { ns: 'translation' })} />
                    ) : (
                        sortedSessionsData.map((meeting: SessionsDataResponseType, i: number) => {
                            if (
                                !filteringState.selectedYear ||
                                meeting.datum.includes(filteringState.selectedYear.code)
                            ) {
                                return <MeetingCard meetingData={meeting} key={i} />;
                            }
                            return null;
                        })
                    )}
                </div>
            </>
        );
    }
}

export default Meetings;
